export const DEFAULT_NUMBER_ANIMATION_DURATION_MS = 650

/** Formato predeterminado: entero redondeado con separadores de miles en español. */
export function formatAnimatedNumber(value) {
  return Math.round(value).toLocaleString('es')
}

/**
 * Anima un valor numérico en el navegador sin depender de React.
 * `onUpdate` recibe cada valor intermedio; con movimiento reducido o un destino
 * no finito o una duración no positiva se entrega el valor final (null para un
 * destino inválido) sin animar.
 * Si inicia una animación, devuelve una función para cancelar los frames y retirar el listener.
 *
 * Ejemplo en cualquier módulo JS:
 * const cancel = animateNumber({ from: 0, to: 100, onUpdate: updateCounter })
 * // Llamar a cancel?.() al desmontar la vista o reemplazar la animación.
 */
export function animateNumber({
  from = 0,
  to,
  duration = DEFAULT_NUMBER_ANIMATION_DURATION_MS,
  onUpdate
}) {
  const target = Number.isFinite(to) ? to : null
  const motion = window.matchMedia?.('(prefers-reduced-motion: reduce)')

  if (target == null || duration <= 0 || motion?.matches) {
    onUpdate(target)
    return undefined
  }

  let frame
  let started

  const tick = (now) => {
    started ??= now
    const progress = Math.min(1, (now - started) / duration)
    const easedProgress = 1 - (1 - progress) ** 3
    const currentValue = from + (target - from) * easedProgress
    onUpdate(currentValue)

    if (progress < 1) {
      frame = requestAnimationFrame(tick)
    }
  }

  frame = requestAnimationFrame(tick)

  const onMotionChange = (event) => {
    if (!event.matches) return
    cancelAnimationFrame(frame)
    onUpdate(target)
  }

  motion?.addEventListener?.('change', onMotionChange)

  return () => {
    cancelAnimationFrame(frame)
    motion?.removeEventListener?.('change', onMotionChange)
  }
}
